import { useCallback } from "react";
import { message } from "antd";

import { Handbook } from "features/handbook/model/handbook";
import {
  useAddServiceToAllMutation,
  useEditHandbookMutation,
  useRemoveServiceFromAllMutation,
} from "features/handbook/api/handbookApi";
import { buildUpdatePayloadFromRow } from "features/handbook/lib/buildUpdatePayloadFromRow";
import { collectServiceCodes } from "features/handbook/lib/serviceCodes";
import { useConfirmAddAll } from "shared/hooks/useConfirmAddAll";
import { useConfirmDeleteAll } from "shared/hooks/useConfirmDeleteAll";
import { ALL_SERVICES_CODE } from "./useHandbookRoute";

type Args = {
  dictCode?: string;
  serviceCode?: string;
  /** Название сервиса — подставляется в текст подтверждения массовых операций. */
  serviceTitle: string;
  /** Привязка меняет оба списка, поэтому перезапрашиваем их вместе. */
  reloadBoth: () => Promise<unknown>;
};

/** Привязка записей справочника к сервису — поштучно и целым списком. */
export function useServiceBinding({
  dictCode,
  serviceCode,
  serviceTitle,
  reloadBoth,
}: Args) {
  const confirmAddAll = useConfirmAddAll();
  const confirmDeleteAll = useConfirmDeleteAll();

  const [edit] = useEditHandbookMutation();
  const [addServiceToAll, { isLoading: isAddingAll }] =
    useAddServiceToAllMutation();
  const [removeServiceFromAll, { isLoading: isRemovingAll }] =
    useRemoveServiceFromAllMutation();

  const canBindAll =
    !!dictCode && !!serviceCode && serviceCode !== ALL_SERVICES_CODE;

  /** Привязка и отвязка — это обычный PUT: меняется только список кодов сервисов. */
  const updateServiceCodes = useCallback(
    async (
      row: Handbook,
      nextCodes: string[],
      okText: string,
      errorText: string,
    ) => {
      const body = buildUpdatePayloadFromRow(row, { serviceCodes: nextCodes });

      try {
        await edit({ slug: dictCode!, body }).unwrap();
        await reloadBoth();
        message.success(okText);
      } catch {
        message.error(errorText);
      }
    },
    [dictCode, edit, reloadBoth],
  );

  const attach = useCallback(
    async (row: Handbook) => {
      if (!dictCode || !serviceCode) return;

      const existing = collectServiceCodes(row);
      const nextCodes = existing.includes(serviceCode)
        ? existing
        : [...existing, serviceCode];

      await updateServiceCodes(
        row,
        nextCodes,
        "Успешно привязано",
        "Не удалось привязать",
      );
    },
    [dictCode, serviceCode, updateServiceCodes],
  );

  const detach = useCallback(
    async (row: Handbook) => {
      if (!dictCode || !serviceCode) return;

      const existing = new Set(collectServiceCodes(row));
      existing.delete(serviceCode);

      await updateServiceCodes(
        row,
        Array.from(existing),
        "Успешно отвязано",
        "Не удалось отвязать",
      );
    },
    [dictCode, serviceCode, updateServiceCodes],
  );

  const attachAll = useCallback(() => {
    if (!canBindAll) return;

    confirmAddAll({
      title: "Вы уверены что хотите добавить все записи?",
      content: `Все доступные записи будут привязаны к сервису "${serviceTitle}".`,
      onOk: async () => {
        try {
          await addServiceToAll({
            slug: dictCode!,
            serviceCode: serviceCode!,
          }).unwrap();
          await reloadBoth();
          message.success("Все записи добавлены в сервис");
        } catch (e) {
          console.error(e);
          message.error("Не удалось добавить все");
        }
      },
    });
  }, [
    canBindAll,
    dictCode,
    serviceCode,
    confirmAddAll,
    serviceTitle,
    addServiceToAll,
    reloadBoth,
  ]);

  const detachAll = useCallback(() => {
    if (!canBindAll) return;

    confirmDeleteAll({
      title: "Отвязать все записи?",
      content: `Все записи будут отвязаны от сервиса "${serviceTitle}".`,
      onOk: async () => {
        try {
          await removeServiceFromAll({
            slug: dictCode!,
            serviceCode: serviceCode!,
          }).unwrap();
          await reloadBoth();
          message.success("Все записи отвязаны от сервиса");
        } catch (e) {
          console.error(e);
          message.error("Не удалось отвязать все");
        }
      },
    });
  }, [
    canBindAll,
    dictCode,
    serviceCode,
    confirmDeleteAll,
    serviceTitle,
    removeServiceFromAll,
    reloadBoth,
  ]);

  return {
    attach,
    detach,
    attachAll,
    detachAll,
    isAddingAll,
    isRemovingAll,
  };
}
